import React, { useEffect, useState, useCallback } from 'react';
import {
  LogIn,
  LogOut,
  MessageSquare,
  Bot,
  CheckCircle,
  FileText,
  MousePointerClick,
  Eye,
  UserCog,
  ShoppingCart,
  type LucideIcon,
  AlertCircle,
} from 'lucide-react';
import { BrandedLoading } from '@/components/common/BrandedLoading';
import { createApiService } from '@/services/api';

const analyticsApi = createApiService(
  import.meta.env.VITE_ANALYTICS_API_BASE_URL || '/api/analytics'
);

/** Maps event types to display config. */
const EVENT_DISPLAY: Record<string, { icon: LucideIcon; label: string; color: string }> = {
  USER_LOGIN: { icon: LogIn, label: 'Logged in', color: 'text-green-500' },
  USER_LOGOUT: { icon: LogOut, label: 'Logged out', color: 'text-gray-500' },
  SESSION_START: { icon: LogIn, label: 'Session refreshed', color: 'text-green-400' },
  AI_QUERY_SUBMITTED: { icon: MessageSquare, label: 'Coach conversation', color: 'text-purple-500' },
  AI_RESPONSE_RECEIVED: { icon: Bot, label: 'AI response', color: 'text-blue-500' },
  FEATURE_COMPLETED: { icon: CheckCircle, label: 'Completed lesson', color: 'text-emerald-500' },
  FEATURE_ACCESSED: { icon: FileText, label: 'Accessed feature', color: 'text-indigo-500' },
  BUTTON_CLICK: { icon: MousePointerClick, label: 'Action', color: 'text-orange-500' },
  PAGE_VIEW: { icon: Eye, label: 'Viewed page', color: 'text-sky-500' },
  FORM_SUBMIT: { icon: UserCog, label: 'Profile update', color: 'text-teal-500' },
  ERROR_OCCURRED: { icon: AlertCircle, label: 'Error', color: 'text-red-500' },
  CAREER_ASSESSMENT: { icon: FileText, label: 'Career assessment', color: 'text-violet-500' },
  PURCHASE_COMPLETED: { icon: ShoppingCart, label: 'Purchase', color: 'text-amber-500' },
};

const DEFAULT_DISPLAY = { icon: FileText, label: 'Activity', color: 'text-muted-foreground' };

interface ActivityEntry {
  id: string;
  eventType: string;
  eventName: string;
  timestamp: string;
  source?: string;
  pagePath?: string;
  pageTitle?: string;
  featureName?: string;
}

interface ActivityTimelineProps {
  userId: string;
  maxItems?: number;
}

function timeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay === 1) return 'Yesterday';
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function getDisplayInfo(entry: ActivityEntry) {
  const display = EVENT_DISPLAY[entry.eventType] ?? DEFAULT_DISPLAY;
  // Build a human-readable description
  let description = display.label;
  if (entry.pageTitle && entry.eventType === 'PAGE_VIEW') {
    description = `Viewed ${entry.pageTitle}`;
  } else if (entry.featureName && entry.eventType === 'FEATURE_COMPLETED') {
    description = `Completed ${entry.featureName}`;
  } else if (entry.eventType === 'AI_QUERY_SUBMITTED') {
    description = 'Chatted with your AI Coach';
  } else if (entry.eventType === 'AI_RESPONSE_RECEIVED' && entry.pageTitle) {
    description = entry.pageTitle;
  } else if (display === DEFAULT_DISPLAY) {
    // For unmapped event types, use pageTitle or eventName as description
    if (entry.pageTitle && entry.pageTitle !== 'undefined') {
      description = entry.pageTitle;
    } else if (entry.eventName && entry.eventName !== 'undefined') {
      // Convert SNAKE_CASE event name to readable: ONBOARDING_COMPLETED → Onboarding completed
      description = entry.eventName
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/^\w/, (c) => c.toUpperCase());
    }
  }
  return { ...display, description };
}

export const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ userId, maxItems = 20 }) => {
  const [activities, setActivities] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchActivities = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(false);
    try {
      // Try user-profile-service activity API first (has actual data with snake_case fields)
      const profileApi = createApiService(
        import.meta.env.VITE_USER_PROFILE_API_BASE_URL || '/api/user-profile'
      );
      const resp = await profileApi.get<{ content?: Record<string, unknown>[] }>(
        `/api/v1/profile/activities/user/${encodeURIComponent(userId)}/paginated?page=0&size=${maxItems}&sort=createdAt,desc`
      ).catch(() => null);

      if (resp) {
        const data = (resp as Record<string, unknown>) ?? {};
        const raw = (data.content ?? data.data ?? data) as Record<string, unknown>[];
        if (Array.isArray(raw) && raw.length > 0) {
          // Map snake_case profile-service fields to our ActivityEntry
          const mapped: ActivityEntry[] = raw.map((r) => ({
            id: String(r.id ?? r._id ?? ''),
            eventType: String(r.activityType ?? r.activity_type ?? r.eventType ?? 'OTHER'),
            eventName: String(r.activityName ?? r.activity_name ?? r.eventName ?? ''),
            timestamp: String(r.createdAt ?? r.created_at ?? r.timestamp ?? ''),
            source: String(r.source ?? 'five-user-profile-service'),
            pagePath: String(r.pagePath ?? r.page_path ?? ''),
            pageTitle: String(r.description ?? r.pageTitle ?? ''),
            featureName: String(r.featureName ?? r.feature_name ?? ''),
          }));
          setActivities(mapped);
          return;
        }
      }

      // Fallback: try analytics-metering-service
      const analyticsResp = await analyticsApi.get<{ content?: ActivityEntry[] }>(
        `/api/v1/user-activities/users/${encodeURIComponent(userId)}?page=0&size=${maxItems}&sort=timestamp,desc`
      ).catch(() => null);

      if (analyticsResp) {
        const data = (analyticsResp as Record<string, unknown>) ?? {};
        const content = (data.content ?? data.data ?? data) as ActivityEntry[] | undefined;
        setActivities(Array.isArray(content) ? content : []);
      } else {
        setActivities([]);
      }
    } catch {
      setError(true);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [userId, maxItems]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <BrandedLoading message="Loading activity…" iconSize="sm" layout="inline" />
      </div>
    );
  }

  if (error || activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <FileText className="h-12 w-12 text-muted-foreground/50 mb-3" />
        <p className="text-sm text-muted-foreground font-medium">
          {error ? 'Could not load activity' : 'No activity yet'}
        </p>
        <p className="text-xs text-muted-foreground mt-1 max-w-sm">
          {error
            ? 'The analytics service may be unavailable. Try again later.'
            : 'Logins, lesson completions, and coach conversations will appear here.'}
        </p>
      </div>
    );
  }

  const visibleActivities = expanded ? activities : activities.slice(0, 5);

  return (
    <div className="space-y-1">
      {visibleActivities.map((entry) => {
        const { icon: Icon, color, description } = getDisplayInfo(entry);
        return (
          <div
            key={entry.id}
            className="flex items-start gap-3 py-2 px-1 rounded-lg hover:bg-muted/40 transition-colors"
          >
            <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted ${color}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {description}{entry.eventName && description === 'Activity' ? `: ${entry.eventName}` : ''}
              </p>
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
              {timeAgo(entry.timestamp)}
            </span>
          </div>
        );
      })}
      {!expanded && activities.length > 5 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full text-center text-sm text-primary hover:underline py-2"
        >
          View all ({activities.length})
        </button>
      )}
      {expanded && activities.length > 5 && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="w-full text-center text-sm text-primary hover:underline py-2"
        >
          Show less
        </button>
      )}
    </div>
  );
};

export default ActivityTimeline;
